// components/StackedBarChart.tsx

import React from 'react';
import Chart from 'react-apexcharts';
// ✅ We import the ApexCharts React wrapper so we can use charts in React.

// ------------------ PROPS INTERFACE ------------------
interface BarChartProps {
  categories: string[];           // Labels for X-axis (e.g., ['Jan', 'Feb', 'Mar'])
  series: ApexAxisChartSeries;    // Data series array [{ name: 'Label', data: [...] }]
  colors?: string[];              // Optional: define custom bar colors
  xTitle?: string;                // Optional: Title for X-axis
  yTitle?: string;                // Optional: Title for Y-axis
  height?: number;                // Optional: Chart height (default 300)
  isStacked?: boolean;            // Optional: Enable stacked bar mode (default false)
  toastSuffix?:string;
}

// ------------------ COMPONENT ------------------
const ApexBarChart: React.FC<BarChartProps> = ({
  categories,
  series,
  colors,
  isStacked = false, // default: normal bar chart, not stacked
  xTitle = '',
  yTitle = '',
  height = 300,       // default chart height = 300px
  toastSuffix = ''
  
}) => {
  // ------------------ Y-AXIS FORMATTER ------------------
  const formatYAxis = (val: number) => {
    // ✅ Converts large numbers into readable format (k, L, M)
    if (val >= 1000000) {
      return (val / 1000000).toFixed(val % 1000000 === 0 ? 0 : 1) + 'M';
    } else if (val >= 100000) {
      return (val / 100000).toFixed(val % 100000 === 0 ? 0 : 1) + 'L';
    } else if (val >= 1000) {
      return (val / 1000).toFixed(val % 1000 === 0 ? 0 : 1) + 'k';
    }
    return val.toString(); // if <1000 return as-is
  };

  // ------------------ APEX OPTIONS ------------------
  const options: ApexCharts.ApexOptions = {
    chart: {
      type: 'bar',             // ✅ Chart type = bar
      stacked: isStacked,      // ✅ Enable/disable stacked chart
      toolbar: { show: false },// ✅ Hides the toolbar (download, zoom, pan)
      animations: {
        enabled: true,         // ✅ Turn on chart animations
        speed: 800,            // Animation speed (ms)
        animateGradually: {    // ✅ Bars animate one after another
          enabled: true,
          delay: 150,          // Delay between animations
        },
        dynamicAnimation: {    // ✅ When updating data, animate
          enabled: true,
          speed: 350,          // Speed of data update animation
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,     // ✅ Bars vertical (false) or horizontal (true)
        borderRadius: 0,       // ✅ Rounded corners for bars (0 = square)
        columnWidth: '35%',    // ✅ Width of each bar
      },
    },
    dataLabels: { enabled: false }, // ✅ Hides numbers on top of bars
    xaxis: {
      categories,              // ✅ Labels for X-axis
      title: {
        text: xTitle,          // ✅ Show X-axis title
        style: {
          fontSize: '14px',
          fontFamily: 'intermedium',
          fontWeight: 'normal',
          color: '#8D8D8D',
        },
      },
      labels: {
        show: true,
        style: {
          fontSize: '12px',
          fontFamily: 'interregular',
          fontWeight: 'normal',
        },
        // formatter: (val: string | number) => {
        //   // ✅ Shortens long X-axis labels
        //   const text = String(val);
        //   const maxLength = 10;
        //   return text.length > maxLength ? text.substring(0, maxLength) + '…' : text;
        // },
        formatter: (val: string | number) => String(val), // ✅ show full value

      },
    },
    yaxis: {
      title: {
        text: yTitle,          // ✅ Show Y-axis title
        offsetX: -8,           // ✅ Shift title a little left
        style: {
          fontSize: '14px',
          fontFamily: 'intermedium',
          fontWeight: 'normal',
          color: '#8D8D8D',
        },
      },
      labels: {
        show: true,
        formatter: formatYAxis, // ✅ Apply custom formatter (k, L, M)
        style: {
          fontSize: '12px',
          fontFamily: 'interregular',
          fontWeight: 'normal',
        },
      },
    },
    legend: { show: false },    // ✅ Hide legend (set true to show series labels)
    fill: { opacity: 1 },       // ✅ Make bars solid (no transparency)
    colors,                     // ✅ Apply custom colors if passed
    tooltip: {
      enabled: true,           // ✅ Hide tooltip (set true to show on hover)
      x: {
        show: true,
        formatter: (val: string | number) => {
          return String(val);
        },
      },
      y: {
        formatter: (val: number) => `${val.toLocaleString()} ${toastSuffix}`, // ✅ Format numbers with £ symbol
      },
    },
  };

  // ------------------ RENDER CHART ------------------
  return <Chart options={options} series={series} type="bar" height={height} />;
};

export default ApexBarChart;
